import { TrendingUp } from "lucide-react";
import type { OperationalLoopReport } from "@/lib/api/building";
import { verificationRates } from "@/components/building/optimization/operational-loop/verification";
import { SectionPanel } from "@/components/ui/section-panel";
import { cardSurface, cardToneText, type CardTone } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

// Verifiointi — the loop window's aggregate, three numbers, no rows: over every history step a
// recipe was in force, how often the equipment followed what was written, how often the served
// zones stayed inside the strategy bounds, and the measured fan-energy effect where the KB
// nameplate allows a kWh. Per-instant detail lives on the timeline (the charts and the
// cursor-following Aktiivinen resepti card), not here. "Tarkkuus kasvattaa autonomiabudjettia"
// (product-vision.md → "Action — autonomy earned through verification") is exactly these two
// rates. Numbers come from `report.verification` (services/building/.../operational_loop.py);
// nothing is computed here beyond a percentage.

const num = (n: number, decimals = 1) =>
  n.toLocaleString("fi-FI", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

function rateTone(pct: number | null): CardTone {
  if (pct == null) return "empty";
  return pct >= 90 ? "ok" : "warn";
}

function Kpi({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone: CardTone;
  hint: string;
}) {
  return (
    <div className={cn(cardSurface, "p-3")}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 font-mono text-2xl tabular-nums leading-none",
          cardToneText[tone],
        )}
      >
        {value}
      </div>
      <div className="mt-1.5 text-[11px] text-muted-foreground">{hint}</div>
    </div>
  );
}

export function VerificationStrip({
  report,
}: {
  report: OperationalLoopReport | null;
}) {
  const v = report?.verification ?? null;
  const { controlPct, constraintPct } = verificationRates(v);
  const windowHours = report
    ? (report.window.past_steps * report.window.step_minutes) / 60
    : 24;
  const recipeCount =
    report?.recipe_windows.filter(
      (w) => w.start_index <= report.window.now_index,
    ).length ?? 0;
  const activeHours = v
    ? (v.active_steps * (report?.window.step_minutes ?? 15)) / 60
    : 0;

  return (
    <SectionPanel
      icon={TrendingUp}
      title={`Verifiointi · viim. ${windowHours} h`}
      subtitle="Ohjaus toteutui · rajat pitivät · vaikutus — reseptien voimassaoloaikana mitatuista signaaleista, ei euroista"
    >
      {!v ? (
        <p className="text-sm text-muted-foreground">
          Ei vielä verifioitavaa: laitteella ei ole reseptihistoriaa tässä
          ikkunassa. Toteutunut ohjaus, rajat ja vaikutus näkyvät tässä, kun
          reseptit on ajettu.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Kpi
            label="Ohjaus toteutui"
            value={controlPct == null ? "—" : `${controlPct} %`}
            tone={rateTone(controlPct)}
            hint={
              v.control_checked_steps === 0
                ? "ei mitattavaa: portilla ei ole KB:n mittausparia"
                : `${v.control_followed_steps}/${v.control_checked_steps} askelta · mitattu lähempänä kirjoitettua kuin perusuraa`
            }
          />
          <Kpi
            label="Rajat pitivät"
            value={constraintPct == null ? "—" : `${constraintPct} %`}
            tone={rateTone(constraintPct)}
            hint={
              v.constraint_checked_steps === 0
                ? "ei rajoja: strategiassa ei asetettuja rajoja tai ei tilamittauksia"
                : `${v.constraint_ok_steps}/${v.constraint_checked_steps} askelta · strategian rajat palvelluissa tiloissa`
            }
          />
          <Kpi
            label="Vaikutus"
            value={
              v.effect_kwh == null
                ? "—"
                : `${v.effect_kwh > 0 ? "+" : ""}${num(v.effect_kwh, 2)} kWh`
            }
            tone={
              v.effect_kwh == null ? "empty" : v.effect_kwh <= 0 ? "ok" : "warn"
            }
            hint={
              v.effect_kwh == null
                ? "puhallinsähkö: nimellisteho ei konfiguroitu, kWh ei laskettavissa"
                : `puhallinsähkö mitatusta nopeudesta vs. perusura, ${num(activeHours, 1)} h · ${recipeCount} reseptiä`
            }
          />
        </div>
      )}
    </SectionPanel>
  );
}
